<script>
  /**
   * Hoja de registro rápido.
   *
   * El objetivo de diseño es UNO: registrar un gasto en menos de 5 segundos.
   * La razón está medida — la fricción al registrar es la causa número uno de
   * que la gente abandone una app de finanzas. Si anotar cuesta, no anotas; si
   * no anotas, los números mienten; si los números mienten, borras la app.
   *
   * Por eso:
   *  · Teclado propio, con teclas grandes. El teclado del sistema tapa media
   *    pantalla y tiene las teclas pensadas para escribir texto, no cifras.
   *  · Tecla "000". En Colombia casi todo termina en tres ceros. Es una tecla
   *    que ahorra tres toques en casi cada registro.
   *  · La cuenta y la categoría vienen preseleccionadas con lo último que
   *    usaste. En el caso común no hay que tocarlas.
   *  · Guardar está activo desde que hay un monto. Todo lo demás es opcional.
   */
  import { formatear, formatearAria, interpretar, calcularGmf } from '../lib/dinero.js';
  import { TIPO_MOVIMIENTO, METODO_PAGO, TIPO_CUENTA, crearMovimiento, hoy } from '../lib/db.js';
  import { guardar } from '../lib/repositorio.js';
  import { gmfDe, movimientoGmf } from '../lib/gmf.js';

  let {
    cuentas = [],
    categorias = [],
    entidades = [],
    abierta = $bindable(false),
    alGuardar = () => {},
  } = $props();

  /** El elemento <dialog> nativo. Ver la nota en el marcado. */
  let dialogo = $state(null);

  let tipo = $state(TIPO_MOVIMIENTO.EGRESO);
  let digitos = $state('');
  let categoriaId = $state(null);
  let cuentaOrigenId = $state(null);
  let cuentaDestinoId = $state(null);
  let concepto = $state('');
  let guardando = $state(false);
  let fecha = $state(hoy());
  let metodoPago = $state(METODO_PAGO.EFECTIVO);
  let gmfManual = $state(null);
  // La hora es opcional. Sirve para el orden dentro del día: pagar el seguro
  // en la mañana y recibir los honorarios en la tarde no es lo mismo.
  let hora = $state('');   // si el usuario lo corrige, manda lo suyo

  const monto = $derived(Number(digitos || 0));
  const esTransferencia = $derived(tipo === TIPO_MOVIMIENTO.TRANSFERENCIA);
  const esIngreso = $derived(tipo === TIPO_MOVIMIENTO.INGRESO);

  // Solo las categorías del tipo que se está registrando, y solo las hojas
  // del árbol: mostrar "Transporte" junto a "Gasolina" confunde.
  const categoriasVisibles = $derived(
    categorias.filter(
      (c) => c.tipo === (esIngreso ? 'ingreso' : 'egreso') && c.padre_id && !c.archivada
    )
  );

  const cuentaOrigen = $derived(cuentas.find((c) => c.id === cuentaOrigenId));

  /**
   * ¿Este movimiento paga 4x1000?
   *
   * La regla salió de la hoja de cálculo que Esteban ya venía llevando:
   *
   *   =SI(Y(A12<0; IZQUIERDA(D12;3)<>"PSE"); A12*0,004; 0)
   *
   * O sea: solo sobre salidas, y los PSE no pagan. Sus propios datos lo
   * confirman — "PSE ADDI" y "PSE NEQUI" salen en cero mientras "PAGO ADRIAN"
   * sí cobra. A eso se le suman dos reglas más:
   *
   *   · El efectivo no paga: el impuesto es sobre movimientos bancarios.
   *   · La cuenta marcada como exenta no paga, hasta su tope mensual.
   *
   * Se calcula solo, se muestra, y se puede corregir a mano. Nunca decide
   * en silencio.
   */
  // La regla vive en lib/gmf.js: la misma para registrar, prestar y pagar
  // cuotas. Antes estaba copiada aquí, y las pantallas nuevas no la usaban.
  const gmfCalculado = $derived(gmfDe({
    tipo,
    monto,
    cuentaOrigen: esIngreso ? null : cuentaOrigen,
    entidad: entidades.find((e) => e.id === cuentaOrigen?.entidad_id) ?? null,
    metodoPago,
  }));
  const gmfAutomatico = $derived(esIngreso ? 0 : gmfCalculado.monto);

  const gmf = $derived(gmfManual ?? gmfAutomatico);

  /** Atajos de fecha: la mayoría de registros son de hoy o de ayer. */
  function fechaRelativa(dias) {
    const d = new Date();
    d.setDate(d.getDate() + dias);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  const esHoyOAyer = $derived(fecha === fechaRelativa(0) || fecha === fechaRelativa(-1));

  /**
   * Lo que dice el tercer chip.
   * Cuando la fecha elegida ya es hoy o ayer, no tiene sentido repetir la
   * palabra: ese chip pasa a ser la invitación a escoger otro día.
   */
  const etiquetaFecha = $derived(
    esHoyOAyer
      ? 'Otra fecha'
      : new Date(fecha + 'T12:00:00').toLocaleDateString('es-CO', {
          day: 'numeric', month: 'short', year: 'numeric',
        })
  );

  const METODOS = [
    [METODO_PAGO.EFECTIVO, 'Efectivo'],
    [METODO_PAGO.TRANSFERENCIA, 'Transferencia'],
    [METODO_PAGO.PSE, 'PSE'],
    [METODO_PAGO.QR, 'QR'],
    [METODO_PAGO.DEBITO, 'Débito'],
    [METODO_PAGO.CREDITO, 'Crédito'],
    [METODO_PAGO.DEBITO_AUTOMATICO, 'Débito automático'],
  ];

  const puedeGuardar = $derived(
    monto > 0 &&
      !guardando &&
      (esTransferencia
        ? cuentaOrigenId && cuentaDestinoId && cuentaOrigenId !== cuentaDestinoId
        : true)
  );

  // Valores por defecto: la primera cuenta y, en transferencia, la segunda.
  $effect(() => {
    if (cuentas.length && !cuentaOrigenId) cuentaOrigenId = cuentas[0].id;
    if (cuentas.length > 1 && !cuentaDestinoId) cuentaDestinoId = cuentas[1].id;
  });

  // Abrir con showModal() es lo que activa el comportamiento nativo: atrapa el
  // foco dentro de la hoja, cierra con Escape y oscurece el fondo con
  // ::backdrop. Todo eso habría que escribirlo a mano con un <div>.
  $effect(() => {
    if (!dialogo) return;
    if (abierta && !dialogo.open) dialogo.showModal();
    if (!abierta && dialogo.open) dialogo.close();
  });

  function tecla(t) {
    // Un golpecito corto confirma el toque sin mirar la pantalla. Es lo que
    // hace que el teclado se sienta físico en vez de pintado.
    if (navigator.vibrate) navigator.vibrate(8);

    if (t === 'borrar') {
      digitos = digitos.slice(0, -1);
      return;
    }
    // Tope de 12 dígitos: por encima de eso es un error de dedo, no un monto.
    if (digitos.length >= 12) return;
    if (digitos === '' && t === '000') return;
    digitos = (digitos + t).replace(/^0+(?=\d)/, '');
  }

  /**
   * Pegar desde el portapapeles.
   * Sirve cuando copias el monto de la app del banco. Pasa por el mismo
   * intérprete que entiende "30 mil", "$ 250.000,00" y "una luca".
   */
  async function pegar() {
    try {
      const texto = await navigator.clipboard.readText();
      const valor = interpretar(texto);
      if (valor) digitos = String(Math.abs(valor));
    } catch {
      // Si el navegador no da permiso, no pasa nada: se escribe a mano.
    }
  }

  function cerrar() {
    abierta = false;
    digitos = '';
    concepto = '';
    gmfManual = null;
    hora = '';
    // La fecha NO se reinicia: si estás poniéndote al día registrando cosas
    // de agosto, sería absurdo tener que volver a escoger agosto cada vez.
    guardando = false;
  }

  async function confirmar() {
    if (!puedeGuardar) return;
    guardando = true;
    try {
      const movimiento = crearMovimiento({
        tipo,
        monto,
        fecha,
        // La hora solo se pone si el movimiento es de hoy. Inventarle las
        // 3 de la tarde a un gasto de hace tres semanas es dato falso.
        hora: hora || (fecha === fechaRelativa(0)
          ? new Date().toTimeString().slice(0, 5)
          : null),
        concepto: concepto.trim(),
        metodo_pago: metodoPago,
        gmf,
        gmf_revisado: true,   // la persona vio la cifra antes de guardar
        categoria_id: esTransferencia ? null : categoriaId,
        cuenta_origen: esIngreso ? null : cuentaOrigenId,
        cuenta_destino: esIngreso ? cuentaOrigenId : esTransferencia ? cuentaDestinoId : null,
      });
      await guardar('movimientos', movimiento);

      // El 4x1000 se guarda como movimiento HIJO, no como columna del padre.
      // Cuesta una fila más, pero el saldo queda bien sin lógica especial y
      // el informe anual de comisiones sale gratis.
      if (gmf > 0) {
        await guardar('movimientos', crearMovimiento(movimientoGmf(movimiento, gmf)));
      }

      if (navigator.vibrate) navigator.vibrate([12, 40, 12]);
      alGuardar(movimiento);
      cerrar();
    } finally {
      guardando = false;
    }
  }

  const TECLAS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '000', '0', 'borrar'];
</script>

<!--
  Se usa el <dialog> nativo en vez de un <div> con role="dialog".
  Gratis y sin JavaScript extra: atrapa el foco dentro, cierra con Escape,
  oscurece el fondo con ::backdrop y lo anuncian bien los lectores de pantalla.
  Un <div> disfrazado de diálogo exige escribir todo eso a mano, y casi
  siempre queda a medias.
-->
<dialog
  bind:this={dialogo}
  class="hoja"
  aria-label="Registrar movimiento"
  onclose={() => { abierta = false; }}
  onclick={(e) => { if (e.target === dialogo) cerrar(); }}
>
  {#if abierta}
    <button class="agarradera" onclick={cerrar} aria-label="Cerrar"></button>

    <!-- Tipo de movimiento -->
    <div class="tipos" role="tablist">
      {#each [[TIPO_MOVIMIENTO.EGRESO, 'Gasto'], [TIPO_MOVIMIENTO.INGRESO, 'Ingreso'], [TIPO_MOVIMIENTO.TRANSFERENCIA, 'Traslado']] as [valor, etiqueta]}
        <button
          role="tab"
          class="tipo"
          class:activo={tipo === valor}
          data-tipo={valor}
          aria-selected={tipo === valor}
          onclick={() => { tipo = valor; categoriaId = null; }}
        >
          {etiqueta}
        </button>
      {/each}
    </div>

    <!-- El monto, en grande -->
    <div class="monto" data-tipo={tipo}>
      <span class="cifra" aria-hidden="true">
        {monto > 0 ? formatear(monto) : '$ 0'}
      </span>
      <span class="solo-lectores">{formatearAria(monto)}</span>
    </div>

    <!-- Fecha. Sin esto no hay forma de ponerse al día con lo de semanas
         pasadas, que es justo lo que uno necesita al empezar. -->
    <div class="fila-fecha">
      <button
        class="chip"
        class:activo={fecha === fechaRelativa(0)}
        onclick={() => (fecha = fechaRelativa(0))}
      >Hoy</button>
      <button
        class="chip"
        class:activo={fecha === fechaRelativa(-1)}
        onclick={() => (fecha = fechaRelativa(-1))}
      >Ayer</button>
      <label class="chip-fecha" class:activo={!esHoyOAyer}>
        {etiquetaFecha}
        <input type="date" bind:value={fecha} max={fechaRelativa(0)} aria-label="Fecha" />
      </label>
      <label class="chip-fecha" class:activo={Boolean(hora)}>
        {hora || 'Hora'}
        <input type="time" bind:value={hora} aria-label="Hora (opcional)" />
      </label>
    </div>

    <!-- Traslado: se ve de dónde a dónde, para que quede claro que NO es gasto -->
    {#if esTransferencia}
      <div class="traslado">
        <select bind:value={cuentaOrigenId} aria-label="Cuenta de origen">
          {#each cuentas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
        </select>
        <span class="flecha" aria-hidden="true">→</span>
        <select bind:value={cuentaDestinoId} aria-label="Cuenta de destino">
          {#each cuentas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
        </select>
      </div>
      {#if cuentaOrigenId === cuentaDestinoId}
        <p class="aviso">Escoge dos cuentas distintas.</p>
      {:else}
        <p class="nota">Un traslado entre tus cuentas no es un gasto: tu plata sigue siendo la misma.</p>
      {/if}
    {:else}
      <!-- Cuenta y categoría: tocar aquí es opcional -->
      <div class="fila-selectores">
        <select bind:value={cuentaOrigenId} aria-label="Cuenta">
          {#each cuentas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
        </select>
        <select bind:value={categoriaId} aria-label="Categoría">
          <option value={null}>Sin categoría</option>
          {#each categoriasVisibles as c}<option value={c.id}>{c.nombre}</option>{/each}
        </select>
      </div>
    {/if}

    <input
      class="concepto"
      type="text"
      bind:value={concepto}
      placeholder="¿En qué fue? (opcional)"
      aria-label="Concepto"
      enterkeyhint="done"
    />

    <!-- Método de pago. Importa más de lo que parece: define si paga 4x1000. -->
    {#if !esIngreso}
      <div class="metodos">
        {#each METODOS as [valor, etiqueta]}
          <button
            class="chip"
            class:activo={metodoPago === valor}
            onclick={() => { metodoPago = valor; gmfManual = null; }}
          >{etiqueta}</button>
        {/each}
      </div>

      <!-- El 4x1000, visible y corregible. Nunca se cobra en silencio. -->
      {#if monto > 0 && cuentaOrigen && cuentaOrigen.tipo !== 'efectivo'}
        <div class="gmf">
          {#if gmf > 0}
            <span>4x1000: <strong class="cifra">{formatear(gmf)}</strong></span>
            <button class="enlace" onclick={() => (gmfManual = 0)}>No me lo cobraron</button>
          {:else}
            <span class="sin-gmf">
              Sin 4x1000
              {#if gmfManual === 0}(dijiste que no te lo cobraron){:else if gmfCalculado.razon}({gmfCalculado.razon}){/if}
            </span>
            {#if gmfManual === 0 && gmfCalculado.monto > 0}
              <button class="enlace" onclick={() => (gmfManual = null)}>Sí me lo cobraron</button>
            {:else if gmfCalculado.monto === 0}
              <!-- Nequi pasado de su tope, un banco que sí cobra el PSE: la
                   regla no lo sabe todo, así que se puede agregar a mano. -->
              <button class="enlace" onclick={() => (gmfManual = calcularGmf(monto))}>Sí me lo cobraron</button>
            {/if}
          {/if}
        </div>
      {/if}
    {/if}

    <!-- Teclado propio -->
    <div class="teclado">
      {#each TECLAS as t}
        <button
          class="tecla"
          class:ancha={t === '000'}
          onclick={() => tecla(t)}
          aria-label={t === 'borrar' ? 'Borrar' : t}
        >
          {#if t === 'borrar'}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 5H20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9L3 12l6-7Z"
                    stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
              <path d="M12 10l4 4M16 10l-4 4"
                    stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
            </svg>
          {:else}
            {t}
          {/if}
        </button>
      {/each}
    </div>

    <div class="acciones">
      <button class="secundario" onclick={pegar}>Pegar monto</button>
      <button class="principal" disabled={!puedeGuardar} onclick={confirmar}>
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
    </div>
  {/if}
</dialog>

<style>
  .hoja {
    /* El <dialog> nativo trae márgenes automáticos para centrarse. Aquí la
       queremos pegada abajo, al alcance del pulgar. */
    margin: auto auto 0;
    padding: var(--e-2) var(--e-4) calc(var(--e-4) + var(--safe-b));
    width: 100%;
    max-width: var(--w-app);
    max-height: 92dvh;
    border: none;
    background: var(--bg-surface);
    border-radius: var(--r-2xl) var(--r-2xl) 0 0;
    box-shadow: var(--sombra-hoja);
    color: var(--ink-1);
    overflow-y: auto;
  }

  .hoja[open] {
    display: flex;
    flex-direction: column;
    gap: var(--e-3);
    /* Solo se anima `transform`: es lo único que el navegador puede mover sin
       recalcular el diseño de la página. Animar `height` o `top` produce
       tirones en un celular de gama media. */
    animation: subir var(--d-normal) var(--ease-entrada);
  }

  /* El fondo oscuro lo pinta el navegador. No hace falta un div de velo. */
  .hoja::backdrop {
    background: var(--velo);
    animation: aparecer var(--d-rapida) var(--ease-entrada);
  }

  @keyframes aparecer { from { opacity: 0 } to { opacity: 1 } }
  @keyframes subir {
    from { transform: translateY(100%) }
    to   { transform: translateY(0) }
  }

  .agarradera {
    width: 40px;
    height: 4px;
    border-radius: var(--r-full);
    background: var(--border-strong);
    margin: var(--e-1) auto var(--e-2);
    opacity: 0.5;
  }

  /* ── Tipo de movimiento ── */
  .tipos {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--e-1);
    background: var(--bg-sunken);
    padding: var(--e-1);
    border-radius: var(--r-full);
  }
  .tipo {
    padding: var(--e-2);
    border-radius: var(--r-full);
    font-size: var(--t-sm);
    font-weight: var(--w-semibold);
    color: var(--ink-2);
    transition: background var(--d-rapida) var(--ease-estandar),
                color var(--d-rapida) var(--ease-estandar);
  }
  .tipo.activo {
    background: var(--bg-surface);
    color: var(--ink-1);
    box-shadow: var(--sombra-1);
  }
  .tipo.activo[data-tipo='ingreso']       { color: var(--ingreso) }
  .tipo.activo[data-tipo='transferencia'] { color: var(--transferencia) }

  /* ── El monto ── */
  .monto {
    text-align: center;
    font-size: var(--t-4xl);
    font-weight: var(--w-bold);
    letter-spacing: var(--track-tight);
    line-height: var(--lh-tight);
    padding: var(--e-3) 0 var(--e-1);
    color: var(--ink-1);
    /* El gasto NO lleva color: el 90% de los registros son gastos y un muro
       rojo anula el significado del rojo. Se colorea la excepción. */
  }
  .monto[data-tipo='ingreso']       { color: var(--ingreso) }
  .monto[data-tipo='transferencia'] { color: var(--transferencia) }

  /* ── Selectores ── */
  .fila-selectores,
  .traslado {
    display: flex;
    gap: var(--e-2);
    align-items: center;
  }
  .fila-selectores select,
  .traslado select {
    flex: 1;
    min-width: 0;
    padding: var(--e-2) var(--e-3);
    /* 16px mínimo: por debajo de eso, iOS hace zoom solo al enfocar. */
    font-size: var(--t-base);
    background: var(--bg-sunken);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
  }
  .flecha { color: var(--transferencia); font-size: var(--t-lg) }

  .nota, .aviso {
    font-size: var(--t-xs);
    text-align: center;
    color: var(--ink-3);
    margin-top: calc(var(--e-1) * -1);
  }
  .aviso { color: var(--alerta) }

  .concepto {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-sunken);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
  }

  /* ── Fecha y método ── */
  .fila-fecha { display: flex; gap: var(--e-2); align-items: center }

  .metodos {
    display: flex;
    gap: var(--e-1);
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 2px;
  }
  .metodos::-webkit-scrollbar { display: none }

  .chip,
  .chip-fecha {
    flex: 0 0 auto;
    padding: var(--e-2) var(--e-3);
    border-radius: var(--r-full);
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
    color: var(--ink-2);
    background: var(--bg-sunken);
    border: 1px solid transparent;
    min-height: var(--tap-min);
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    transition: background var(--d-rapida) var(--ease-estandar),
                color var(--d-rapida) var(--ease-estandar);
  }
  .chip.activo,
  .chip-fecha.activo {
    background: var(--acento-suave);
    color: var(--acento);
    border-color: var(--acento);
  }

  /* El input de fecha se esconde detrás del chip: al tocarlo abre el
     calendario nativo del sistema, que es el que la gente ya sabe usar. */
  .chip-fecha { position: relative; cursor: pointer }
  .chip-fecha input {
    position: absolute;
    inset: 0;
    opacity: 0;
    width: 100%;
    cursor: pointer;
  }

  /* ── 4x1000 ── */
  .gmf {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--e-2);
    font-size: var(--t-sm);
    color: var(--ink-2);
    padding: var(--e-2) var(--e-3);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
  }
  .sin-gmf { color: var(--ink-3) }
  .enlace {
    font-size: var(--t-xs);
    color: var(--acento);
    text-decoration: underline;
    padding: var(--e-1);
  }

  /* ── Teclado ── */
  .teclado {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--e-2);
  }
  .tecla {
    /* 56px: por encima del mínimo de 48 que piden las guías táctiles. Aquí se
       escriben cifras de dinero; equivocarse de tecla cuesta. */
    min-height: 56px;
    font-size: var(--t-xl);
    font-weight: var(--w-medium);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
    display: grid;
    place-items: center;
    transition: transform var(--d-instant) var(--ease-salida),
                background var(--d-instant) var(--ease-estandar);
  }
  .tecla:active {
    transform: scale(0.94);
    background: var(--border);
  }

  /* ── Acciones ── */
  .acciones {
    display: flex;
    gap: var(--e-2);
    margin-top: var(--e-1);
  }
  .secundario {
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-md);
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
    color: var(--ink-2);
    background: var(--bg-sunken);
    min-height: var(--tap);
  }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    font-size: var(--t-base);
    font-weight: var(--w-semibold);
    color: var(--on-acento);
    background: var(--acento);
    min-height: var(--tap);
    transition: opacity var(--d-rapida) var(--ease-estandar),
                transform var(--d-instant) var(--ease-salida);
  }
  .principal:active:not(:disabled) { transform: scale(0.98) }
  .principal:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
</style>
